import { useState, useEffect } from "react";
import { Plus, Edit, Trash2, ExternalLink, FileDown, Download, ToggleLeft, ToggleRight } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import FileUpload from "@/components/FileUpload";

const CATEGORIES = ["Primary TET", "Upper Primary TET", "C-TET", "Group C", "Group D", "SSC", "Banking", "General"];

export default function AdminEbooks() {
  const [ebooks, setEbooks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: "", mrp: "", price: "", description: "", image_url: "", category: "General",
    razorpay_url: "", pages: "", author: "", file_format: "PDF", file_size: "", download_url: ""
  });

  useEffect(() => { fetchEbooks(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchEbooks = async () => {
    const { data } = await api.get("/ebooks");
    setEbooks(data);
  };

  const resetForm = () => ({
    title: "", mrp: "", price: "", description: "", image_url: "", category: "General",
    razorpay_url: "", pages: "", author: "", file_format: "PDF", file_size: "", download_url: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, mrp: parseFloat(form.mrp) || 0, price: parseFloat(form.price) || 0 };
    if (editing) {
      await api.put(`/ebooks/${editing}`, payload);
    } else {
      await api.post("/ebooks", payload);
    }
    setShowForm(false);
    setEditing(null);
    setForm(resetForm());
    fetchEbooks();
  };

  const handleEdit = (ebook) => {
    setForm({
      title: ebook.title, mrp: ebook.mrp, price: ebook.price, description: ebook.description || "",
      image_url: ebook.image_url || "", category: ebook.category || "General",
      razorpay_url: ebook.razorpay_url || "", pages: ebook.pages || "", author: ebook.author || "",
      file_format: ebook.file_format || "PDF", file_size: ebook.file_size || "", download_url: ebook.download_url || ""
    });
    setEditing(ebook.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this e-book?")) {
      await api.delete(`/ebooks/${id}`);
      fetchEbooks();
    }
  };

  const inputClass = "w-full admin-input border-b-2 px-4 py-3 text-sm focus:outline-none focus:border-[#0284C7] transition-none";

  return (
    <AdminLayout>
      <div data-testid="admin-ebooks-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black admin-text flex items-center gap-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              <FileDown size={24} className="text-[#7C3AED]" />
              E-Books (Soft Copy)
            </h1>
            <p className="text-sm admin-text-secondary mt-0.5">Manage downloadable PDF / E-Books with Razorpay payment page links</p>
          </div>
          <button
            data-testid="add-ebook-btn"
            onClick={() => { setShowForm(!showForm); setEditing(null); setForm(resetForm()); }}
            className="bg-[#7C3AED] text-white font-bold py-2.5 px-4 text-xs flex items-center gap-1.5 transition-none"
          >
            <Plus size={14} /> Add E-Book
          </button>
        </div>

        {/* How it works */}
        {!showForm && ebooks.length === 0 && (
          <div className="admin-card border p-5 mb-6">
            <h3 className="text-sm font-bold admin-text mb-3">How to add E-Books:</h3>
            <ol className="text-xs admin-text-secondary space-y-2 list-decimal list-inside">
              <li>Go to <strong>Razorpay Dashboard &rarr; Payment Pages</strong> and create a payment page for each e-book</li>
              <li>Copy the <strong>Razorpay Payment Page URL</strong> (e.g., https://rzp.io/l/your-ebook)</li>
              <li>Click <strong>"Add E-Book"</strong> and fill in book details + cover image</li>
              <li>Paste the Razorpay URL in the <strong>"Razorpay Payment Page URL"</strong> field</li>
              <li>Optionally add a <strong>Download URL</strong> (Google Drive / Dropbox link) for post-purchase delivery</li>
              <li>Students will see a <strong>"Pay & Download Now"</strong> button that opens your Razorpay payment page</li>
            </ol>
          </div>
        )}

        {/* Add/Edit Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="admin-card border p-5 mb-6 space-y-4" data-testid="ebook-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">E-Book Title</label>
                <input data-testid="eb-title" placeholder="e.g., C-TET Practice Questions PDF" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputClass} required />
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Author</label>
                <input data-testid="eb-author" placeholder="Author name" value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">MRP (Original Price)</label>
                <input data-testid="eb-mrp" type="number" placeholder="499" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} className={inputClass} required />
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Selling Price</label>
                <input data-testid="eb-price" type="number" placeholder="199" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputClass} required />
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Category</label>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputClass}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Pages</label>
                <input placeholder="e.g., 200 pages" value={form.pages} onChange={(e) => setForm({ ...form, pages: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">File Format</label>
                <select value={form.file_format} onChange={(e) => setForm({ ...form, file_format: e.target.value })} className={inputClass}>
                  <option value="PDF">PDF</option>
                  <option value="EPUB">EPUB</option>
                  <option value="DOCX">DOCX</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">File Size</label>
                <input placeholder="e.g., 15 MB" value={form.file_size} onChange={(e) => setForm({ ...form, file_size: e.target.value })} className={inputClass} />
              </div>
            </div>

            <div>
              <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Description</label>
              <textarea placeholder="E-Book description..." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={`${inputClass} h-20`} />
            </div>

            <FileUpload
              label="E-Book Cover Image"
              accept="image/*"
              uploadType="ebooks"
              currentUrl={form.image_url}
              onUpload={(url) => setForm({ ...form, image_url: url })}
            />
            <div>
              <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Or Paste Cover Image URL</label>
              <input placeholder="Image URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className={inputClass} />
            </div>

            {/* Razorpay URL */}
            <div className="bg-purple-50 border border-purple-200 p-4">
              <label className="text-xs font-bold text-purple-700 flex items-center gap-1.5 mb-2">
                <ExternalLink size={14} /> Razorpay Payment Page URL
              </label>
              <input
                data-testid="eb-razorpay-url"
                placeholder="https://rzp.io/l/your-ebook-payment-link"
                value={form.razorpay_url}
                onChange={(e) => setForm({ ...form, razorpay_url: e.target.value })}
                className={`${inputClass} border-purple-300 focus:border-purple-500`}
                required
              />
              <p className="text-[10px] text-purple-600 mt-1">
                Create a payment page at <strong>Razorpay Dashboard &rarr; Payment Pages</strong> and paste the URL. This opens when students click "Pay & Download Now".
              </p>
            </div>

            {/* Download URL (optional) */}
            <div>
              <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Download URL (Optional - for post-purchase delivery)</label>
              <input
                placeholder="Google Drive / Dropbox link to the PDF file"
                value={form.download_url}
                onChange={(e) => setForm({ ...form, download_url: e.target.value })}
                className={inputClass}
              />
              <p className="text-[10px] admin-text-secondary mt-1">This link can be shared after purchase for direct download.</p>
            </div>

            <div className="flex gap-2">
              <button data-testid="eb-submit" type="submit" className="bg-[#7C3AED] text-white font-bold py-3 px-6 text-sm transition-none">
                {editing ? "Update" : "Add"} E-Book
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="admin-card border font-bold py-3 px-6 text-sm transition-none admin-text">
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* E-Books List */}
        <div className="space-y-3" data-testid="ebooks-list">
          {ebooks.map((ebook) => {
            const discount = ebook.mrp > ebook.price ? Math.round(((ebook.mrp - ebook.price) / ebook.mrp) * 100) : 0;
            return (
              <div key={ebook.id} className="admin-card border p-4 flex gap-4">
                <div className="w-16 aspect-[3/4] bg-slate-200 flex-shrink-0 overflow-hidden">
                  {ebook.image_url ? <img src={ebook.image_url} alt="" className="w-full h-full object-cover" /> : (
                    <div className="w-full h-full bg-[#7C3AED]/10 flex items-center justify-center"><FileDown size={20} className="text-[#7C3AED]" /></div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-sm font-bold admin-text">{ebook.title}</h3>
                      <p className="text-[10px] admin-text-secondary">{ebook.author || "—"} &middot; {ebook.category} &middot; {ebook.file_format} &middot; {ebook.file_size || "—"}</p>
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => handleEdit(ebook)} className="p-1.5 admin-text-secondary"><Edit size={14} /></button>
                      <button onClick={() => handleDelete(ebook.id)} className="p-1.5 text-red-400"><Trash2 size={14} /></button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-black text-base admin-text">&#8377;{ebook.price}</span>
                    {discount > 0 && <>
                      <span className="text-xs admin-text-secondary line-through">&#8377;{ebook.mrp}</span>
                      <span className="bg-green-600 text-white text-[10px] font-bold px-1.5 py-0.5">{discount}%</span>
                    </>}
                  </div>
                  {ebook.razorpay_url && (
                    <a href={ebook.razorpay_url} target="_blank" rel="noreferrer" className="text-[10px] font-bold text-[#7C3AED] flex items-center gap-1 mt-1">
                      <ExternalLink size={10} /> Payment Link
                    </a>
                  )}
                </div>
              </div>
            );
          })}
          {ebooks.length === 0 && !showForm && <p className="text-sm admin-text-secondary py-4 text-center">No e-books yet.</p>}
        </div>
      </div>
    </AdminLayout>
  );
}
